<!-- Week — the seven-day min, max and rain range.

     Same instrument, longer paper. Each row is a temperature span drawn against
     ONE scale shared by the whole week, so a cold Thursday is visibly shorter
     and further left than a warm Sunday; a per-row scale would make every day
     look identical, which is the usual mistake. Rain is the same two variables
     as the trace — chance as the number, millimetres as the ink. -->
<template>
  <IonPage>
    <IonContent :fullscreen="true" class="week">
      <div class="sheet">
        <header class="head">
          <h1>Week</h1>
          <p class="meta">{{ place ? place.name : 'No place set' }}</p>
        </header>

        <ul v-if="days.length" class="days">
          <li v-for="d in days" :key="d.date" class="day">
            <p class="when">{{ dayName(d.date) }}</p>

            <div class="span" :aria-label="`${Math.round(d.minC)} to ${Math.round(d.maxC)} degrees`">
              <span class="low">{{ Math.round(d.minC) }}</span>
              <span class="bar">
                <span class="fill" :style="barStyle(d)" />
              </span>
              <span class="high">{{ Math.round(d.maxC) }}</span>
            </div>

            <p class="rain">
              <span class="chance">{{ Math.round(d.chance) }}%</span>
              <span class="mm" :style="{ opacity: mmInk(d) }">{{ mmRange(d) }}</span>
            </p>

            <p class="summary">{{ d.summary }}</p>
          </li>
        </ul>

        <p v-else class="caption warn">
          The seven-day forecast has not come through. The trace on Now still works.
        </p>

        <div class="actions">
          <button class="act quiet" type="button" @click="router.push({ name: 'now' })">
            Back to now
          </button>
        </div>

        <p class="source">Sample data. No forecast provider is connected.</p>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import '../chrome.js';
import { place, weekDays } from '../weather.js';

const router = useRouter();
const days = computed(() => weekDays.value);

// ONE scale for the week. Padded by a degree so the coldest morning is still a
// visible sliver rather than nothing at all.
const scale = computed(() => {
  if (!days.value.length) return { lo: 0, hi: 1 };
  const lo = Math.min(...days.value.map((d) => Number(d.minC))) - 1;
  const hi = Math.max(...days.value.map((d) => Number(d.maxC))) + 1;
  return { lo, hi: hi > lo ? hi : lo + 1 };
});

function barStyle(d) {
  const { lo, hi } = scale.value;
  const span = hi - lo;
  const left = ((Number(d.minC) - lo) / span) * 100;
  const width = ((Number(d.maxC) - Number(d.minC)) / span) * 100;
  return { left: `${left}%`, width: `${Math.max(width, 2)}%` };
}

// The trace's rule, applied to a day: volume is carried by ink density, so a
// 12 mm Tuesday reads darker than a 1 mm Wednesday at the same chance.
const mmInk = (d) => Math.min(1, 0.35 + (Number(d.mmHigh) || 0) * 0.07);

const mmRange = (d) => {
  const lo = Number(d.mmLow) || 0;
  const hi = Number(d.mmHigh) || 0;
  if (hi <= 0) return '—';
  return lo === hi ? `${hi.toFixed(1)} mm` : `${lo.toFixed(1)}–${hi.toFixed(1)} mm`;
};

const dayName = (date) => new Date(`${date}T00:00:00`)
  .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
</script>

<style scoped>
.week { --background: var(--oh-paper, #dfe3d1); }
.sheet {
  --paper: var(--oh-paper, #dfe3d1);
  --grid: var(--oh-grid, #b0522f);
  --ink: var(--oh-ink, #1b2540);
  --rain: var(--oh-rain, #35689f);
  --signal: var(--oh-signal, #a8331a);
  background: var(--paper);
  min-height: 100%;
}
.head { padding: env(safe-area-inset-top, 0px) 14px 0; }
.head h1 {
  margin: 16px 0 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-style: italic;
  font-weight: 400;
  font-size: 27px;
  line-height: 1.1;
  color: var(--ink);
}
.meta { margin: 3px 0 0; }
.meta,
.caption,
.source,
.when,
.rain,
.span {
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 11px;
  letter-spacing: 0.06em;
  color: color-mix(in srgb, var(--ink) 60%, transparent);
}
.days { list-style: none; margin: 14px 0 0; padding: 0; }
.day {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2px 12px;
  padding: 12px 14px;
  border-top: 1px solid color-mix(in srgb, var(--grid) 22%, transparent);
}
.day:last-child { border-bottom: 1px solid color-mix(in srgb, var(--grid) 22%, transparent); }
.when { grid-column: 1; margin: 0; color: var(--ink); }
.rain { grid-column: 2; grid-row: 1; margin: 0; display: flex; gap: 8px; justify-content: flex-end; }
.chance { color: var(--ink); }
.mm { color: var(--rain); font-weight: 500; }
.span {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 0;
}
.low,
.high { color: var(--ink); min-width: 22px; }
.high { text-align: right; }
.bar {
  position: relative;
  flex: 1;
  height: 3px;
  background: color-mix(in srgb, var(--grid) 18%, transparent);
}
.fill { position: absolute; top: 0; height: 3px; background: var(--ink); }
.summary {
  grid-column: 1 / -1;
  margin: 6px 0 0;
  font-family: var(--oh-serif, Georgia, serif);
  font-size: 14px;
  color: color-mix(in srgb, var(--ink) 80%, transparent);
}
.caption { margin: 24px 14px; line-height: 1.6; }
.caption.warn { color: var(--signal); }
.actions { display: flex; gap: 8px; padding: 16px 14px 8px; }
.act {
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: 2px;
  background: transparent;
  color: var(--ink);
  font-family: var(--oh-mono, ui-monospace, monospace);
  font-size: 12px;
  letter-spacing: 0.06em;
  opacity: 0.65;
  cursor: pointer;
}
.act:focus-visible { outline: 2px solid var(--ink); outline-offset: 2px; }
.source {
  margin: 0;
  padding: 0 14px calc(20px + env(safe-area-inset-bottom, 0px));
  font-size: 10px;
  line-height: 1.6;
  color: color-mix(in srgb, var(--ink) 45%, transparent);
}
</style>
